/**
 * raw_material.repository.ts — 원재료 단가 이력(raw_materials) Firestore 격리 layer.
 *
 * 논리 키: (code, ym) 복합. Firestore 문서 ID는 자동 또는 ${code}__${ym}.
 * Vanilla 호환을 위해 자동 ID + (code, ym) 인덱스 패턴 채택.
 */
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import { tcol } from "@/domain/tenantPath";
import type { CurrentPrice, MaterialCategory, RawMaterialPrice } from "@/domain/material";

const COL = "raw_materials";

function toDateStr(v: unknown): string {
  if (!v) return "";
  if (v instanceof Timestamp) return v.toDate().toISOString().slice(0, 10);
  if (typeof v === "string") return v.slice(0, 10);
  return "";
}

function fromFirestore(snap: QueryDocumentSnapshot<DocumentData>): RawMaterialPrice {
  const d = snap.data();
  const updatedAt = d.updated_at;
  const ps = d.pricing_source;
  return {
    id: snap.id,
    code: d.code ?? "",
    ym: d.ym ?? "",
    effective_date: toDateStr(d.effective_date),
    price: Number(d.price ?? 0),
    name: d.name ?? undefined,
    updated_at: updatedAt instanceof Timestamp ? updatedAt.toDate() : undefined,
    pricing_source:
      ps === "wavg" || ps === "wavg_simple" || ps === "manual" ? ps : undefined,
    wavg_window: typeof d.wavg_window === "number" ? d.wavg_window : undefined,
    wavg_basis: typeof d.wavg_basis === "number" ? d.wavg_basis : undefined,
    wavg_fallback: typeof d.wavg_fallback === "boolean" ? d.wavg_fallback : undefined,
    wavg_auto: typeof d.wavg_auto === "boolean" ? d.wavg_auto : undefined,
  };
}

export const rawMaterialRepo = {
  watchAll(cb: (rows: RawMaterialPrice[]) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      collection(db, tcol(COL)),
      (snap) => {
        const rows = snap.docs.map(fromFirestore);
        rows.sort((a, b) =>
          a.effective_date < b.effective_date ? -1 : a.effective_date > b.effective_date ? 1 : a.code.localeCompare(b.code),
        );
        cb(rows);
      },
      onError,
    );
  },

  /** 단일 행 upsert. id가 있으면 update, 없으면 신규 생성 */
  async upsert(row: RawMaterialPrice): Promise<void> {
    const { id, updated_at: _ignored, ...rest } = row;
    void _ignored;
    const ref = id ? doc(db, tcol(COL), id) : doc(collection(db, tcol(COL)));
    await setDoc(ref, { ...rest, updated_at: Timestamp.now() }, { merge: true });
  },

  /** 여러 행 일괄 (gasCall: master.raw_materials.upsert_batch 대응) */
  async upsertBatch(rows: RawMaterialPrice[]): Promise<void> {
    const batch = writeBatch(db);
    const now = Timestamp.now();
    for (const row of rows) {
      const { id, updated_at: _ignored, ...rest } = row;
      void _ignored;
      const ref = id ? doc(db, tcol(COL), id) : doc(collection(db, tcol(COL)));
      batch.set(ref, { ...rest, updated_at: now }, { merge: true });
    }
    await batch.commit();
  },

  async remove(id: string): Promise<void> {
    await deleteDoc(doc(db, tcol(COL), id));
  },

  /** 특정 기간(ym) 행 일괄 삭제 (gasCall: master.raw_materials.delete_period) */
  async deletePeriod(ym: string): Promise<number> {
    const snap = await getDocs(query(collection(db, tcol(COL)), where("ym", "==", ym)));
    if (snap.empty) return 0;
    const batch = writeBatch(db);
    for (const d of snap.docs) batch.delete(d.ref);
    await batch.commit();
    return snap.size;
  },

  /** 기간 라벨 일괄 변경 (gasCall: master.raw_materials.rename_periods) */
  async renamePeriods(map: { from: string; to: string; effective_date?: string }[]): Promise<number> {
    let total = 0;
    for (const { from, to, effective_date } of map) {
      const snap = await getDocs(query(collection(db, tcol(COL)), where("ym", "==", from)));
      if (snap.empty) continue;
      const batch = writeBatch(db);
      const now = Timestamp.now();
      for (const d of snap.docs) {
        const patch: DocumentData = { ym: to, updated_at: now };
        if (effective_date) patch.effective_date = effective_date;
        batch.set(d.ref, patch, { merge: true });
      }
      await batch.commit();
      total += snap.size;
    }
    return total;
  },
};

/**
 * categories + raw_materials 데이터로 "현재 적용단가" 도출.
 * 순수 함수 — 컴포넌트에서 useMemo로 wrap.
 *
 * @param asOf 기준일 (YYYY-MM-DD). 기본 오늘.
 * @param includeFuture true면 적용예정 단가도 upcoming에 포함
 */
export function deriveCurrentPrices(
  cats: MaterialCategory[],
  prices: RawMaterialPrice[],
  asOf: string = new Date().toISOString().slice(0, 10),
  includeFuture: boolean = true,
): CurrentPrice[] {
  const byCode = new Map<string, RawMaterialPrice[]>();
  for (const p of prices) {
    if (!byCode.has(p.code)) byCode.set(p.code, []);
    byCode.get(p.code)!.push(p);
  }

  const result: CurrentPrice[] = cats.map((c) => {
    const list = (byCode.get(c.code) ?? []).slice().sort((a, b) =>
      a.effective_date < b.effective_date ? -1 : a.effective_date > b.effective_date ? 1 : 0,
    );
    const past = list.filter((p) => p.effective_date <= asOf);
    const future = list.filter((p) => p.effective_date > asOf);
    const cur = past[past.length - 1];
    const upcoming = includeFuture && future.length > 0 ? future[0] : undefined;
    return {
      code: c.code,
      name: c.name,
      category: c.category,
      sort_order: c.sort_order,
      price: cur?.price ?? null,
      effective_date: cur?.effective_date ?? null,
      upcoming: upcoming
        ? { price: upcoming.price, effective_date: upcoming.effective_date }
        : undefined,
    };
  });

  // sort_order 우선, 없으면 code
  result.sort((a, b) => {
    const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return a.code.localeCompare(b.code);
  });
  return result;
}
